import styled from 'styled-components'

import { blue } from '../../../styles/colors'

/** Right-rail layout for review 360 workspace. */
export const REVIEW_RAIL_INSET_PX = 24
export const REVIEW_RAIL_BOTTOM_PX = 28
/** Save cluster (44+96+44) + gap + tracking column. */
export const REVIEW_RAIL_SAVE_COL_WIDTH_PX = 184
export const REVIEW_RAIL_SAVE_COL_AI_WIDTH_PX = 232
export const REVIEW_RAIL_COL_GAP_PX = 8
export const REVIEW_RAIL_TRACKING_COL_WIDTH_PX = 300
export const REVIEW_RAIL_WIDTH_PX =
  REVIEW_RAIL_SAVE_COL_WIDTH_PX +
  REVIEW_RAIL_COL_GAP_PX +
  REVIEW_RAIL_TRACKING_COL_WIDTH_PX
export const REVIEW_RAIL_AI_WIDTH_PX =
  REVIEW_RAIL_SAVE_COL_AI_WIDTH_PX +
  REVIEW_RAIL_COL_GAP_PX +
  REVIEW_RAIL_TRACKING_COL_WIDTH_PX
export const REVIEW_RAIL_SECTION_GAP_PX = 8
export const REVIEW_MINIMAP_MAP_HEIGHT_PX = 300

export const reviewRailBodyMaxHeightPx = () =>
  `calc(100vh - ${
    REVIEW_RAIL_INSET_PX +
    REVIEW_MINIMAP_MAP_HEIGHT_PX +
    62 +
    REVIEW_RAIL_SECTION_GAP_PX +
    REVIEW_RAIL_BOTTOM_PX
  }px)`

export const PageWrap = styled.div`
  padding: 8px 32px 24px;
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-height: calc(100vh - 170px);
  min-width: 0;
  overflow: hidden;
  position: relative;
`

export const ListBusyOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
`

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;
`

export const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #1a1a1a;
`

export const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  margin: 12px 0 18px;
`

export const FilterLink = styled.button<{ $active?: boolean }>`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 14px;
  color: ${props => (props.$active ? blue : '#686868')};
  font-weight: ${props => (props.$active ? 600 : 400)};
  text-decoration: ${props => (props.$active ? 'underline' : 'none')};
`

export const TableScroll = styled.div`
  flex: 1;
  min-width: 0;
  background: #fff;
  overflow-x: auto;
`

export const Table = styled.table`
  width: 100%;
  min-width: max(100%, max-content);
  border-collapse: collapse;
  background: #fff;
  font-size: 13px;
  color: #222;
`

export const Th = styled.th`
  text-align: left;
  font-weight: 600;
  color: #444;
  padding: 10px 12px;
  border-bottom: 1px solid #e6e6e6;
  white-space: nowrap;
`

export const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
  white-space: nowrap;
`

export const StatusDot = styled.span<{ $status: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props =>
    props.$status === 'completed'
      ? '#2eb06c'
      : props.$status === 'in_progress'
        ? '#ef7e46'
        : '#7C7C7C'};
`

export const IconButton = styled.button<{ $disabled?: boolean }>`
  background: none;
  border: none;
  padding: 4px;
  cursor: ${props => (props.$disabled ? 'default' : 'pointer')};
  opacity: ${props => (props.$disabled ? 0.35 : 1)};
  display: inline-flex;
  align-items: center;
  justify-content: center;

  img {
    display: block;
    width: 20px;
    height: 20px;
  }
`

export const ActionButton = styled.button`
  background: ${blue};
  color: #fff;
  border: none;
  border-radius: 2px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
  min-width: 88px;
`

export const StartContinueButton = styled(ActionButton)`
  width: 100%;
  margin-top: 10px;
  min-height: 36px;
  font-weight: 600;
`

export const ReviewSubmissionsButton = styled(StartContinueButton)`
  background: #fff;
  color: ${blue};
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.08);
`

export const ActButtonStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
`

export const Menu = styled.div`
  position: absolute;
  right: 0;
  top: 28px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 180px;
  z-index: 20;
  padding: 6px 0;
`

export const MenuItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 13px;
  color: #222;

  &:hover {
    background: #f4f6f8;
  }
`

export const DateStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
  line-height: 1.25;
  white-space: nowrap;
  color: #444;

  span {
    display: flex;
    gap: 6px;
  }

  em {
    font-style: normal;
    color: #888;
    min-width: 52px;
  }
`

export const StatsInline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
  line-height: 1.25;
  white-space: nowrap;
  color: #444;

  span {
    display: flex;
    gap: 6px;
  }

  em {
    font-style: normal;
    color: #888;
    min-width: 58px;
  }
`

export const StatsPopover = styled.div`
  position: absolute;
  right: 0;
  top: 28px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 220px;
  z-index: 20;
  padding: 12px 14px;
  font-size: 13px;
  color: #333;
  white-space: normal;
`

export const CellRelative = styled.div`
  position: relative;
  display: inline-flex;
`

export const EditCheckList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 180px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid #ddd;
  background: #fff;
  min-width: 140px;
`

export const DisabledCheck = styled.input`
  accent-color: #9a9a9a;
`

export const EmptyState = styled.div`
  padding: 32px 12px;
  color: #686868;
  text-align: center;
`

export const LoadMoreWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 12px 8px;
`

export const StubWrap = styled.div`
  padding: 48px 32px;
  text-align: center;
  color: #444;
`

export const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  cursor: pointer;
`

export const DropdownToggle = styled.button`
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 2px;
  padding: 4px 8px;
  font-size: 13px;
  min-width: 88px;
  text-align: left;
  cursor: pointer;
`

export const SaveButton = styled.button`
  background: ${blue};
  color: #fff;
  border: none;
  border-radius: 2px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 6px;
`

export const CancelButton = styled.button`
  background: none;
  border: 1px solid #ccc;
  border-radius: 2px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
`

export const CheckListPop = styled(EditCheckList)`
  position: absolute;
  left: 0;
  top: 28px;
  z-index: 21;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
`

export const WorkspaceRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 118px);
  min-height: 0;
  background: #111;
  color: #fff;
`

export const WorkspaceChrome = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  background: #073c7a;
  flex-shrink: 0;
`

export const ChromeGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`

export const ChromeBack = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  padding: 4px 0;
`

export const ChromeTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
`

export const ChromeMeta = styled.div`
  font-size: 13px;
  opacity: 0.85;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const ChromeButton = styled.button<{ $primary?: boolean }>`
  background: ${props => (props.$primary ? '#fff' : 'transparent')};
  color: ${props => (props.$primary ? '#073c7a' : '#fff')};
  border: 1px solid #fff;
  border-radius: 2px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
  min-width: 72px;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`

export const WorkspaceBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`

export const SplitArea = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  background: #000;
`

export const PaneWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 0;
`

export const PaneLabel = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 5;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 2px;
  pointer-events: none;
`

export const EmptyPane = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ccc;
  font-size: 14px;
  padding: 24px;
  text-align: center;
`

export const SidePanel = styled.div`
  position: relative;
  width: 320px;
  flex-shrink: 0;
  background: #fff;
  color: #222;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-left: 1px solid #ddd;
`

export const SidePanelHeader = styled.div`
  padding: 12px 14px 8px;
  font-weight: 600;
  font-size: 14px;
  border-bottom: 1px solid #eee;
`

export const ItemList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
`

export const ItemRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 14px;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: #f4f6f8;
  }
`

export const ItemSwatch = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${props => props.$color};
  margin-top: 3px;
  flex-shrink: 0;
`

export const ConfirmBar = styled.div`
  padding: 12px 14px;
  border-top: 1px solid #eee;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(7, 60, 122, 0.24);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
`

export const ConfirmCard = styled.div`
  background: #fff;
  color: #222;
  width: 360px;
  max-width: calc(100% - 32px);
  border-radius: 4px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
  padding: 18px 18px 14px;
`

export const LogModalCard = styled.div`
  background: #fff;
  color: #222;
  width: min(1100px, calc(100% - 48px));
  max-height: calc(100vh - 64px);
  border-radius: 4px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
  padding: 18px 18px 14px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`

export const LogModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`

export const LogModalActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

export const LogTableScroll = styled.div`
  overflow: auto;
  max-height: calc(100vh - 200px);
  border: 1px solid #e6e6e6;
  border-radius: 4px;
`

export const LogTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

export const LogTh = styled.th`
  text-align: left;
  padding: 8px 10px;
  background: #f7f7f7;
  border-bottom: 1px solid #e6e6e6;
  position: sticky;
  top: 0;
  z-index: 1;
  white-space: nowrap;
  font-weight: 600;
`

export const LogTd = styled.td`
  padding: 8px 10px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
  white-space: nowrap;
`

export const LogPauseTd = styled(LogTd)`
  background: #d7ebff;
`

export const MinimapOverlay = styled.div<{ $expanded?: boolean }>`
  position: absolute;
  right: 16px;
  bottom: 16px;
  z-index: 8;
  width: ${props => (props.$expanded ? '420px' : '240px')};
  height: ${props => (props.$expanded ? '320px' : '160px')};
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
`

export const MinimapToggle = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 9;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 2px;
  font-size: 11px;
  padding: 2px 6px;
  cursor: pointer;
`

export const MinimapFloorLabel = styled.div`
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 9;
  max-width: calc(100% - 72px);
  background: rgba(255, 255, 255, 0.92);
  color: #1a1a1a;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: none;
`

export const FloorViewRoot = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vh - 131px);
  min-height: 0;
`

export const FloorViewMap = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
`

export const FloorBackButton = styled.button`
  align-self: flex-start;
  border: 1px solid ${blue};
  border-radius: 4px;
  background: ${blue};
  color: #fff;
  cursor: pointer;
  padding: 8px 14px;
  margin: 8px 8px 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

export const FloorViewControls = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 11;
  display: flex;
  flex-direction: column;
  gap: 0;
`

export const FloorStatRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-width: 0;
`

export const FloorStatName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const FloorStatMeta = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  color: #686868;
`

export const TaskProgressBody = styled.div`
  padding: 8px 8px 4px;
  color: #212121;
  font-size: 14px;
`

export const TaskProgressRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
`

export const ReviewPreviewCard = styled.div`
  right: 0;
  top: 24px;
  position: absolute;
  width: 400px;
  z-index: 12;
  background: #fff;
  border: 1px solid #fff;
  border-radius: 2px;
  overflow: hidden;
  filter: drop-shadow(0px 5px 30px rgba(0, 0, 0, 0.25));
`

export const ReviewPreviewHeader = styled.div`
  height: 40px;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #7e858e;
  padding: 0 12px 0 8px;
  border-radius: 2px 2px 0 0;
  font-size: 16px;
`

/** Crops the center of an equirectangular so the card reads as a normal photo. */
export const ReviewPreviewImageWrap = styled.div`
  position: relative;
  height: 220px;
  overflow: hidden;
  background: #fff;
  border-radius: 0 0 2px 2px;
`

export const ReviewPreviewImage = styled.img`
  position: absolute;
  left: 50%;
  top: 48%;
  /* ~100° horizontal of a 360° equirect; poles cropped out. */
  width: 360%;
  height: 160%;
  max-width: none;
  object-fit: cover;
  object-position: center center;
  transform: translate(-50%, -50%);
  display: block;
  background: white;
  pointer-events: none;
`

export const ReviewPreviewEmpty = styled.div`
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  color: #686868;
  border-radius: 0 0 2px 2px;
`

export const ReviewPreviewExpand = styled.button`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: white;
  border: none;
  padding: 8px;
  min-width: 44px;
  min-height: 44px;
  box-shadow: 0 1px 20px rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const PanoRoot = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: #000;
`

export const PanoStage = styled.div`
  position: absolute;
  inset: 0;
`

export const PanoStack = styled.div`
  position: absolute;
  inset: 0;
`

export const PanoLayer = styled.div<{
  $opacity: number
  $interactive?: boolean
  $hidden?: boolean
  $zIndex?: number
}>`
  position: absolute;
  inset: 0;
  opacity: ${props => props.$opacity};
  visibility: ${props => (props.$hidden ? 'hidden' : 'visible')};
  pointer-events: ${props => (props.$interactive ? 'auto' : 'none')};
  z-index: ${props =>
    props.$zIndex ?? (props.$interactive ? 1 : 0)};
`

export const PanoLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
`

export const HistoryControls = styled.div`
  position: fixed;
  left: max(16px, env(safe-area-inset-left, 0px));
  bottom: max(28px, env(safe-area-inset-bottom, 0px));
  z-index: 1204;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`

export const HistoryToolbarRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

export const OpacityStrip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const OpacityStepButton = styled.button<{
  $level: number
  $active?: boolean
  $disabled?: boolean
}>`
  width: 36px;
  height: 36px;
  border-radius: 4px;
  border: 2px solid ${props => (props.$active ? '#fff' : 'transparent')};
  background: rgba(7, 60, 122, ${props => props.$level});
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.$disabled ? 0.35 : 1)};
  padding: 0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
`

export const HistoryToggleButton = styled.button<{
  $active?: boolean
  $disabled?: boolean
}>`
  width: 44px;
  height: 44px;
  border-radius: 4px;
  border: none;
  background: ${props => (props.$active ? blue : '#fff')};
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.$disabled ? 0.45 : 1)};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  padding: 0;

  img {
    width: 24px;
    height: 24px;
  }
`

/** History icon is a single blue asset — invert when the button is active. */
export const HistoryIconButton = styled(HistoryToggleButton)`
  img {
    filter: ${props =>
      props.$active ? 'brightness(0) invert(1)' : 'none'};
  }
`

export const StlOverlayToggleButton = styled(HistoryToggleButton)``

export const StlOffsetControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 8px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid ${blue};
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  min-width: 112px;
`

export const StlOffsetLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: ${blue};
`

export const StlOffsetValue = styled.span`
  flex: 1;
  text-align: right;
  font-variant-numeric: tabular-nums;
  min-width: 36px;
`

export const StlOffsetNudge = styled.button`
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid ${blue};
  border-radius: 2px;
  background: #fff;
  color: ${blue};
  font-size: 14px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: #eef3f9;
  }
`

export const StlOffsetReset = styled.button`
  margin-top: 2px;
  border: none;
  background: transparent;
  color: ${blue};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 0;

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`

export const TrackingDock = styled.div<{
  $expanded?: boolean
  $embedded?: boolean
}>`
  position: ${props => (props.$embedded ? 'relative' : 'fixed')};
  ${props =>
    !props.$embedded
      ? `
    top: ${REVIEW_RAIL_INSET_PX + REVIEW_MINIMAP_MAP_HEIGHT_PX + 62}px;
    right: ${REVIEW_RAIL_INSET_PX}px;
    bottom: ${props.$expanded ? `${REVIEW_RAIL_BOTTOM_PX}px` : 'auto'};
    width: ${REVIEW_RAIL_WIDTH_PX}px;
  `
      : `
    flex: 1;
    min-height: 0;
    width: 100%;
  `}
  z-index: 1203;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  > div {
    margin: 0;
    width: 100%;
    box-sizing: border-box;
    height: ${props =>
      props.$embedded || props.$expanded ? '100%' : 'auto'};
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: ${props => (props.$embedded ? '4px' : '0 0 4px 4px')};
    box-shadow: 0px 8px 20px rgba(106, 106, 106, 0.1);
  }
`

export const TrackingProgress = styled.div`
  padding: 8px 8px 4px;
  border-bottom: 1px solid #e8e8e8;
  margin-bottom: 4px;
`

export const TrackingProgressLabel = styled.div`
  color: #686868;
  opacity: 0.64;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  line-height: 16px;
  margin-bottom: 4px;
`

export const TrackingProgressRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 0 8px;
  color: #212121;
  font-size: 14px;
`

export const TrackingList = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 4px 8px 0;
`

export const TrackingInstalledToggle = styled.button<{ $active?: boolean }>`
  display: block;
  width: 100%;
  margin: 4px 0 8px;
  padding: 6px 8px;
  border: 1px solid ${props => (props.$active ? blue : '#d0d5dd')};
  border-radius: 2px;
  background: ${props => (props.$active ? 'rgba(7, 60, 122, 0.08)' : '#fff')};
  color: ${blue};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
`

export const TrackingTypeBlock = styled.div`
  margin-bottom: 8px;
`

export const TrackingItemRow = styled.label<{
  $muted?: boolean
  $highlighted?: boolean
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px 6px 34px;
  font-size: 13px;
  color: ${props => (props.$muted ? '#8a9199' : '#212121')};
  cursor: pointer;
  background: ${props =>
    props.$highlighted ? '#dce8f5' : 'transparent'};

  &:hover {
    background: ${props => (props.$highlighted ? '#dce8f5' : '#f4f6f8')};
  }
`

export const TrackingItemMain = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1 1 auto;
`

export const TrackingLoadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 8px;
  color: #686868;
  font-size: 13px;
`

export const TrackingCheckbox = styled.input`
  accent-color: ${blue};
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`

export const SaveBarColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`

export const ReviewRightRail = styled.div<{ $aiAssist?: boolean }>`
  position: fixed;
  top: ${REVIEW_RAIL_INSET_PX}px;
  right: ${REVIEW_RAIL_INSET_PX}px;
  bottom: ${REVIEW_RAIL_BOTTOM_PX}px;
  width: ${props =>
    props.$aiAssist ? REVIEW_RAIL_AI_WIDTH_PX : REVIEW_RAIL_WIDTH_PX}px;
  z-index: 1202;
  display: flex;
  flex-direction: column;
  min-height: 0;
  gap: ${REVIEW_RAIL_SECTION_GAP_PX}px;
`

export const ReviewRailSplit = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  gap: ${REVIEW_RAIL_COL_GAP_PX}px;
  align-items: stretch;
  margin-top: 0;
`

export const ReviewRailSaveColumn = styled.div<{ $aiAssist?: boolean }>`
  width: ${props =>
    props.$aiAssist
      ? REVIEW_RAIL_SAVE_COL_AI_WIDTH_PX
      : REVIEW_RAIL_SAVE_COL_WIDTH_PX}px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`

export const ReviewRailTrackingColumn = styled.div`
  width: ${REVIEW_RAIL_TRACKING_COL_WIDTH_PX}px;
  flex-shrink: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`

export const SaveNavGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`

export const SaveCluster = styled.div<{ $aiAssist?: boolean }>`
  display: flex;
  align-items: stretch;
  height: 64px;
  border: 1px solid ${props => (props.$aiAssist ? '#2e7d32' : blue)};
  border-radius: 0;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 20px rgba(0, 0, 0, 0.1);
`

export const SaveVpRow = styled.div`
  display: flex;
  margin-top: 4px;
  width: 100%;
  overflow: visible;
  position: relative;
  z-index: 2;
`

export const SaveVpCell = styled.div<{ $width: number }>`
  width: ${props => props.$width}px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  text-align: center;
  overflow: visible;
`

export const SaveVpLabel = styled.span<{ $current?: boolean }>`
  color: #fff;
  font-size: 11px;
  font-weight: ${props => (props.$current ? 700 : 400)};
  line-height: 1.2;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.55);
  white-space: nowrap;
`

export const SaveStack = styled.div<{ $aiAssist?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 96px;
  border-left: 1px solid ${props => (props.$aiAssist ? '#2e7d32' : blue)};
  border-right: 1px solid ${props => (props.$aiAssist ? '#2e7d32' : blue)};
`

export const SavePrimary = styled.button`
  flex: 1;
  background: ${blue};
  color: #fff;
  border: none;
  border-bottom: 1px solid ${blue};
  border-radius: 0;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;

  &:disabled {
    background: #8fa6c4;
    border-bottom-color: ${blue};
    opacity: 1;
    cursor: default;
  }
`

export const SaveSecondary = styled.button`
  flex: 1;
  background: #fff;
  color: ${blue};
  border: none;
  border-radius: 0;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`

export const NavIconButton = styled.button<{ $aiAssist?: boolean }>`
  background: #fff;
  color: ${props => (props.$aiAssist ? '#2e7d32' : blue)};
  border: none;
  border-radius: 0;
  width: ${props => (props.$aiAssist ? '68px' : '44px')};
  flex-shrink: 0;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`

export const HelpIconButton = styled.button<{ $toolbar?: boolean }>`
  background: ${blue};
  color: #fff;
  border: none;
  border-radius: ${props => (props.$toolbar ? '4px' : '0')};
  width: 44px;
  height: ${props => (props.$toolbar ? '44px' : '64px')};
  flex-shrink: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`

export const ExamplesBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: rgba(0, 0, 0, 0.35);
`

export const ExamplesSheet = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1301;
  max-height: min(85vh, 720px);
  background: #fff;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.18);
  padding: 8px 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
`

export const ExamplesHandle = styled.button`
  align-self: center;
  border: none;
  background: transparent;
  padding: 8px 16px;
  cursor: pointer;
  flex-shrink: 0;

  span {
    display: block;
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background: #c5cdd5;
  }
`

export const ExamplesTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #212121;
  flex-shrink: 0;
`

export const ExamplesEmpty = styled.div`
  font-size: 13px;
  color: #8a9199;
  padding: 12px 0 8px;
`

export const ExamplesGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
`

export const ExamplesGroupTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #5a6570;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`

export const ExamplesRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  overflow-x: auto;
  padding-bottom: 4px;
  -webkit-overflow-scrolling: touch;
`

export const ExamplesCard = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: min(42vw, 420px);
  border-radius: 6px;
  overflow: hidden;
  background: #eef1f4;
`

export const ExamplesImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  background: #eef1f4;
`

export const ExamplesCaption = styled.div`
  position: absolute;
  left: 50%;
  bottom: 10px;
  transform: translateX(-50%);
  max-width: calc(100% - 24px);
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 12px;
  line-height: 1.3;
  text-align: center;
`

export const ReviewMinimapCard = styled.div<{
  $collapsed?: boolean
  $embedded?: boolean
}>`
  position: ${props => (props.$embedded ? 'relative' : 'fixed')};
  top: ${props => (props.$embedded ? 'auto' : `${REVIEW_RAIL_INSET_PX}px`)};
  right: ${props => (props.$embedded ? 'auto' : `${REVIEW_RAIL_INSET_PX}px`)};
  z-index: 1202;
  width: ${props => {
    if (props.$collapsed) {
      return '36px'
    }
    if (props.$embedded) {
      return '100%'
    }
    return `${REVIEW_RAIL_WIDTH_PX}px`
  }};
  flex-shrink: 0;
  filter: drop-shadow(0px 5px 30px rgba(0, 0, 0, 0.25));
  border-radius: ${props => (props.$embedded ? '4px 4px 0 0' : '4px 4px 0 0')};
  overflow: hidden;
`

export const ReviewMinimapHeader = styled.div`
  background: #f8f8f8;
  padding: 11px 16px;
  border-radius: 2px 2px 0 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`

export const ReviewMinimapMap = styled.div`
  height: ${REVIEW_MINIMAP_MAP_HEIGHT_PX}px;
  background: white;
  border-radius: 0 0 2px 2px;
  overflow: hidden;
  position: relative;

  > *:first-child {
    height: 100%;
  }
`

export const ReviewMinimapLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
  z-index: 2;
  pointer-events: none;
`